/**
 * The countdown, as an instrument dial.
 *
 * It was a number in a circle: legible, and obviously the first thing anyone
 * would draw. This is the same information read the way a stopwatch is — a
 * ring that visibly empties, a tick scale to read it against, and the seconds
 * in the middle.
 *
 * The sweep is one CSS animation over the whole countdown rather than a step
 * per second. Stepping is what made the old one feel cheap: a dial that jumps
 * once a second reads as a clock that is broken, where one that drains
 * continuously reads as time actually running out. The number still changes on
 * the second, because that is what the compère counts to.
 */

import { cn } from '../../lib/cn'

/** Viewbox radii. The dial is drawn once and scaled by `size`. */
const R_SWEEP = 96
const R_TICK_OUT = 104
const R_TICK_IN = 88
const R_DASHED = 114
const R_INNER = 62

const TICKS = 60
const SWEEP_CIRCUMFERENCE = 2 * Math.PI * R_SWEEP

export function CountdownDial({
  seconds,
  total,
  size = '30vmin',
  className,
}: {
  /** What the centre reads: 6, 5, 4 … */
  seconds: number
  /** The whole countdown in seconds — how long the ring takes to empty. */
  total: number
  size?: string
  className?: string
}) {
  return (
    <div
      className={cn('relative grid place-items-center', className)}
      style={{ width: size, height: size }}
      role="timer"
      aria-live="off"
      aria-label={`${seconds} seconds`}
    >
      <svg viewBox="-120 -120 240 240" className="size-full">
        <defs>
          <linearGradient id="dial-sweep-stroke" x1="0" y1="1" x2="1" y2="0">
            <stop offset="0%" stopColor="#7BC9D1" />
            <stop offset="55%" stopColor="#F0B84D" />
            <stop offset="100%" stopColor="#FFD16A" />
          </linearGradient>
        </defs>

        {/* Crosshairs, barely there — they give the dial an axis to sit on. */}
        <line x1={-120} y1={0} x2={120} y2={0} stroke="rgba(255,255,255,0.1)" strokeWidth={0.8} />
        <line x1={0} y1={-120} x2={0} y2={120} stroke="rgba(255,255,255,0.1)" strokeWidth={0.8} />

        <circle
          cx={0}
          cy={0}
          r={R_DASHED}
          fill="none"
          stroke="rgba(255,255,255,0.18)"
          strokeWidth={0.9}
          strokeDasharray="2 7"
        />

        {/* The tick scale. Every fifth is long, as on a stopwatch bezel. */}
        {Array.from({ length: TICKS }, (_, i) => {
          const angle = (i / TICKS) * 360 - 90
          const major = i % 5 === 0
          const inner = major ? R_TICK_IN : R_TICK_OUT - 5
          const rad = (angle * Math.PI) / 180

          return (
            <line
              key={i}
              x1={inner * Math.cos(rad)}
              y1={inner * Math.sin(rad)}
              x2={R_TICK_OUT * Math.cos(rad)}
              y2={R_TICK_OUT * Math.sin(rad)}
              stroke={major ? '#7BC9D1' : '#E6A23C'}
              strokeWidth={major ? 2.4 : 1.2}
              opacity={major ? 0.75 : 0.4}
            />
          )
        })}

        <circle cx={0} cy={0} r={R_INNER} fill="none" stroke="rgba(255,255,255,0.12)" strokeWidth={1} />

        {/* The track the sweep runs on. */}
        <circle
          cx={0}
          cy={0}
          r={R_SWEEP}
          fill="none"
          stroke="rgba(255,255,255,0.08)"
          strokeWidth={7}
        />

        {/*
          The sweep itself: a full ring whose dash offset runs from nothing to
          the whole circumference over the countdown, so it empties clockwise
          from twelve o'clock. Linear, because a second has to look like a
          second — easing it would make the last one feel longer than the first.
        */}
        <circle
          className="dial-sweep"
          cx={0}
          cy={0}
          r={R_SWEEP}
          fill="none"
          stroke="url(#dial-sweep-stroke)"
          strokeWidth={7}
          strokeLinecap="round"
          transform="rotate(-90)"
          style={
            {
              strokeDasharray: SWEEP_CIRCUMFERENCE,
              '--dial-circumference': `${SWEEP_CIRCUMFERENCE}`,
              animationDuration: `${total * 1000}ms`,
            } as React.CSSProperties
          }
        />
      </svg>

      {/*
        Zero-padded and tabular, so the digits do not jostle as the count
        falls — "06" to "05" should change the glyph, not the layout.
      */}
      <span
        key={seconds}
        className="dial-count absolute font-extrabold tabular-nums leading-none text-white"
        style={{ fontSize: `calc(${size} * 0.38)` }}
      >
        {String(Math.max(0, seconds)).padStart(2, '0')}
      </span>
    </div>
  )
}
